import React, { useEffect, useState, useCallback } from "react";
import Sidebar from "../components/Sidebar";
import Hero from "../components/Hero";
import LeaderboardTable from "../components/LeaderboardTable";
import RulesDialog from "../components/RulesDialog";
import Footer from "../components/Footer";
import AuthDialog from "../components/AuthDialog";
import BoardSwitcher from "../components/BoardSwitcher";
import { LeaderboardSignupGuide } from '../components/LeaderboardSignupGuide';
import { getCompetitionEnd } from "../mock";
import { loadState, fetchLeaderboard } from "../store";

// Both live sources use the same shape. An empty result must remain empty.
const fromSheet = (sheet, fallbackSettings) => {
  if (!sheet || !Array.isArray(sheet.entries)) {
    return null;
  }
  const sorted = [...sheet.entries].sort((a, b) => a.rank - b.rank);
  return {
    settings: {
      prizePool: sheet.prize_pool ?? fallbackSettings?.prizePool ?? 0,
      affiliateCode: fallbackSettings?.affiliateCode || "GREENVENOM",
    },
    topThree: sorted.filter((e) => e.rank <= 3),
    leaderboard: sorted.filter((e) => e.rank > 3),
    endDate: sheet.end_date || null,
    updatedAt: sheet.updated_at || null,
  };
};

const Leaderboard = () => {
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [rulesOpen, setRulesOpen] = useState(false);
  const [data, setData] = useState(() => loadState());
  const [board, setBoard] = useState("stake");
  const [sheets, setSheets] = useState({ stake: null, exclusive: null });
  const fallbackEnd = React.useMemo(() => getCompetitionEnd(), []);

  const refresh = useCallback(() => setData(loadState()), []);

  const loadSheets = useCallback(async () => {
    const [stake, exclusive] = await Promise.all([
      fetchLeaderboard("stake"),
      fetchLeaderboard("exclusive"),
    ]);
    setSheets({ stake, exclusive });
  }, []);

  useEffect(() => {
    window.addEventListener("gv_data_updated", refresh);
    return () => window.removeEventListener("gv_data_updated", refresh);
  }, [refresh]);

  useEffect(() => {
    loadSheets();
    const id = setInterval(loadSheets, 60000);
    return () => clearInterval(id);
  }, [loadSheets]);

  const openAuth = (mode) => { setAuthMode(mode); setAuthOpen(true); };
  const openRules = () => setRulesOpen(true);

  // Use saved branding, but never substitute demonstration players for live results.
  const storeCurrent =
    board === "exclusive" && data.exclusive
      ? { settings: data.exclusive.settings, topThree: data.exclusive.topThree, leaderboard: data.exclusive.leaderboard }
      : { settings: data.settings, topThree: data.topThree, leaderboard: data.leaderboard };

  const sheetCurrent = fromSheet(sheets[board], storeCurrent.settings);
  const current = sheetCurrent || { settings: { ...storeCurrent.settings, prizePool: 0 }, topThree: [], leaderboard: [] };

  const end = React.useMemo(() => {
    if (sheetCurrent?.endDate) {
      const d = new Date(sheetCurrent.endDate);
      if (!isNaN(d.getTime())) return d;
    }
    return fallbackEnd;
  }, [sheetCurrent, fallbackEnd]);

  return (
    <div className="relative">
      <Sidebar onAuth={openAuth} />

      <div className="content-shift pt-16">
        <main>
          <div className="flex justify-center pt-6 sm:pt-8">
            <BoardSwitcher value={board} onChange={setBoard} />
          </div>

          <Hero
            topThree={current.topThree}
            end={end}
            onRules={openRules}
            settings={current.settings}
            board={board}
            status={!sheets[board] ? 'Loading' : sheets[board].unavailable ? 'Unavailable' : sheets[board].stale ? 'Last saved results' : end.getTime() <= Date.now() ? 'Completed' : new Date(sheets[board].period_start || 0).getTime() > Date.now() ? 'Upcoming' : 'Live Now'}
            periodLabel={sheets[board]?.source === 'stake_api' ? 'Leaderboard' : 'Monthly Leaderboard'}
          />

          <section id="leaderboard" data-testid="leaderboard-rankings-section" className="pt-[52px] pb-16 px-4 sm:px-6 scroll-mt-20">
            {sheets[board]?.unavailable && <p role="alert" className="text-center text-sm text-amber-200 mb-5" data-testid="leaderboard-source-unavailable">Leaderboard results are temporarily unavailable. Please try again shortly.</p>}
            {sheets[board]?.stale && <p role="status" className="text-center text-sm text-amber-200 mb-5" data-testid="leaderboard-source-stale">Showing the last successful results while the source reconnects.</p>}
            {sheets[board] && !sheets[board].unavailable && !current.topThree.length && !current.leaderboard.length && <p className="text-center text-sm text-slate-400 mb-5" data-testid="leaderboard-empty-results">No leaderboard results for this period.</p>}
            <LeaderboardTable rows={current.leaderboard} />
          </section>
          <LeaderboardSignupGuide board={board} onRules={openRules} />
        </main>

        <Footer />
      </div>

      <RulesDialog open={rulesOpen} onOpenChange={setRulesOpen} />

      <AuthDialog
        open={authOpen}
        onOpenChange={setAuthOpen}
        mode={authMode}
        setMode={setAuthMode}
      />
    </div>
  );
};

export default Leaderboard;
